import {EMPTY, Observable, of, ReplaySubject, Subject} from 'rxjs';

import {mergeMap, map, tap, switchMap, take, first, timeout} from 'rxjs/operators';
import {
    BrowserSessionStateId,
    ArtifactId,
    StateDataId,
    ExpeditionId,
    ProjectId,
    DeviceId,
    StateCollectorJobId,
    StateCollectionId,
    StateCollectorSpecId,
    ImageUrl, ScreenshotId
} from '../types/ids';
import {Injectable} from '@angular/core';
import {isDefined} from '../utils/utils';
import {UserService} from './user.service';
import {EventsService} from './events/events.service';
import {BrowserSessionApi} from '../api/rest/browserSession.restapi';
import {AutomationPolicyConfig, BrowserSessionInfo, BrowserSessionStateArtifact, InteractionPath, State} from '../types/browsersession';
import {ArtifactService} from './artifact.service';
import {Artifact} from '../types/artifact';
import {ImageService} from '@webmate-web-core/shared/services/image.service';
import {Browser} from '@webmate-web-core/shared/types/browser';
import {
    BrowserSpecification,
    FactRequest,
    InteractiveDriverSpecification,
    InteractionPathDriverSpecification,
    LiveExpeditionSpec,
    NewStateCollectorJobSpec,
    StateCollection,
    StateCollectorJob, StateCollectorSpec
} from '@webmate-web-core/shared/types/testmgmt';
import {DateTime} from '@webmate-web-core/shared/types/base-types';
import {CDPClient} from '../console/cdp/cdp-client';
import {AuthService} from '@webmate-web-core/shared/services/auth.service';
import {withResultCache} from '@webmate-web-core/shared/utils/cache-util';
import {ApiSourcedPaginationRequest, QueryMetadata} from '@webmate-web-core/shared/api/apibase.api';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import {StateCreationInfo} from '@webmate-web-core/shared/api/rest/browserSession.restapi';


/**
 * Fallback start URL for headless sessions. The backend does not (yet) support starting a session
 * without a start URL, so this is used whenever a caller does not provide one.
 */
const DEFAULT_HEADLESS_START_URL = 'https://www.testfabrik.com';


/**
 * Adapter service towards webmate BrowserSession API.
 */
@Injectable({
    providedIn: 'root'
})
export class BrowserSessionService {

    /** Default max time to wait for an expedition's browser session to become ready/running. */
    static readonly DEFAULT_BROWSER_SESSION_WAIT_TIME = 120 * 1000;

    private _browserSessionInfosById = new Map<string /* BrowserSessionId */, ReplaySubject<BrowserSessionInfo>>();

    private _browserSessionStatesById = new Map<string /* BrowserSessionInfoId */, ReplaySubject<BrowserSessionStateId[]>>();

    private _stateById = new Map<string /* BrowserSessionStateId */, ReplaySubject<State>>();

    private _artifactById: Map<string /* ArtifactId */, Subject<Artifact>> = new Map<string /* ArtifactId */, ReplaySubject<Artifact>>();

    private _stateCollectionById: { [s: string /* StateCollectionId */]: ReplaySubject<StateCollection> };

    private _stateCollectorJobById: { [s: string /* StateCollectorJobId */]: ReplaySubject<StateCollectorJob> };

    constructor(private browserSessionApi: BrowserSessionApi, private artifactService: ArtifactService,
                private userService: UserService, private eventsService: EventsService, private authService: AuthService, private imageService: ImageService) {

        this._stateCollectionById = {};
        this._stateCollectorJobById = {};

        eventsService.getAllEventsOfType(['BrowserSession.StateChanged']).subscribe(event => {
            if (!!event?.session?.id) {
                const cachedExpeditionInfoSubject = this._browserSessionInfosById.get(event.session?.id);
                if (!!cachedExpeditionInfoSubject) {
                    // only update if we have a cached expedition info
                    const browserSessionInfo = BrowserSessionInfo.fromBackendObject(event.session);
                    if (!!browserSessionInfo) {
                        cachedExpeditionInfoSubject.next(browserSessionInfo);
                    }
                }
            }
        });

        eventsService.getAllEventsOfType(['BrowserSession.StateCollection.Updated']).pipe(
            map(event => event.id),
            switchMap(stateCollectionId => {
                // TODO: this is too coarse grained. Not every switchMap should cancel all previous requests but only those specific to the same stateCollectionId.

                const cachedStateCollectionSubject = this._stateCollectionById[stateCollectionId];
                if (!!cachedStateCollectionSubject) {
                    // only update if we have a cached state collection
                    return this.browserSessionApi.getStateCollection(stateCollectionId).pipe(
                        tap(stateCollection => {
                            cachedStateCollectionSubject.next(stateCollection);
                        })
                    );
                } else {
                    return of(undefined);
                }
            })
        ).subscribe();

        eventsService.getAllEventsOfType(['BrowserSession.StateCollectorJob.Updated']).pipe(
            map(event => event.id),
            switchMap(stateCollectorJobId => {
                const cachedStateCollectorJobSubject = this._stateCollectorJobById[stateCollectorJobId];
                if (!!cachedStateCollectorJobSubject) {
                    return this.browserSessionApi.getStateCollectorJob(stateCollectorJobId).pipe(
                        tap(stateCollectorJob => {
                            cachedStateCollectorJobSubject.next(stateCollectorJob);
                        })
                    );
                } else {
                    return of(undefined);
                }
            })
        ).subscribe();

        artifactService.subscribeBrowserSessionStateEndArtifacts$().subscribe(artifact => {
            this.handleBrowserSessionStateEnd(artifact.id);
        });
    }

    getExpeditionsInProject$(projectId: ProjectId, limit: number): ApiSourcedPaginationRequest<BrowserSessionInfo> {
        return this.browserSessionApi.getExpeditionsInProject(projectId, {limit: limit});
    }

    evictState(id: BrowserSessionStateId) {
        let state = this._stateById.get(id);
        if (state) {
            state.complete();
        }
        this._stateById.delete(id);
    }

    evictArtifact(id: ArtifactId) {
        let artifact = this._artifactById.get(id);
        if (artifact) {
            artifact.complete();
        }
        this._artifactById.delete(id);
    }

    /**
     * Trigger state analysis in given expedition.
     */
    analyzeState(expedition: ExpeditionId, requestedFactTypes?: FactRequest[], async?: boolean): Observable<BrowserSessionStateId> {
        return this.browserSessionApi.analyzeState(expedition, requestedFactTypes, async);
    }

    createStateV2(expedition: ExpeditionId, requestedFactTypes: FactRequest[]): Observable<StateCreationInfo> {
        return this.browserSessionApi.createStateV2(expedition, requestedFactTypes);
    }


    /**
     * Return BrowserSessionInfo for Id. This method does not complete the observable. Think of take(1) when using it.
     */
    getBrowserSessionInfo$(id: ExpeditionId): Observable<BrowserSessionInfo> {
        /* Prevent Getting the browser session for a Mock State*/
        if (id === '00000000-0000-0000-0000-000000000000')
            return EMPTY;

        if (!isDefined(this._browserSessionInfosById.get(id))) {
            let sessionSubject = new ReplaySubject<BrowserSessionInfo>(1);
            this._browserSessionInfosById.set(id, sessionSubject);
            return this.browserSessionApi.getBrowserSessionInfo(id).pipe(map((dto) => {
                sessionSubject.next(BrowserSessionInfo.fromBackendObject(dto));
            }), mergeMap(_ => this._browserSessionInfosById.get(id).asObservable()));
        }
        return this._browserSessionInfosById.get(id).asObservable();
    }

    /**
     * Waits for the browser session of the expedition emitted by `expedition$` to become
     * ready/running and emits its {@link BrowserSessionInfo}. Errors with a timeout if the session
     * does not become ready within `maxWaitTime` milliseconds.
     */
    awaitBrowserSessionReady$(expedition$: Observable<ExpeditionId>,
                              maxWaitTime: number = BrowserSessionService.DEFAULT_BROWSER_SESSION_WAIT_TIME): Observable<BrowserSessionInfo> {
        return expedition$.pipe(
            mergeMap(expeditionId => this.getBrowserSessionInfo$(expeditionId).pipe(
                first(info => info.sessionState === 'ready' || info.sessionState === 'running')
            )),
            timeout(maxWaitTime)
        );
    }

    getBrowserSessionInfoNoCache(id: ExpeditionId): Observable<BrowserSessionInfo> {
        if (id === '00000000-0000-0000-0000-000000000000')
            return EMPTY;
        return this.browserSessionApi.getBrowserSessionInfo(id).pipe(map((dto) => {
            let ret = BrowserSessionInfo.fromBackendObject(dto);
            const cached = this._browserSessionInfosById.get(id);
            if (isDefined(cached)) {
                cached.next(ret);
            }
            return ret;
        }));

    }

    getBrowserSessionStates(id: ExpeditionId, refresh: boolean = false): Observable<BrowserSessionStateId[]> {
        if (id === '00000000-0000-0000-0000-000000000000') return of([]);

        if (refresh) {
            this._browserSessionStatesById.get(id)?.complete();
            this._browserSessionStatesById.delete(id);
        }
        if (isDefined(this._browserSessionStatesById.get(id))) return this._browserSessionStatesById.get(id).asObservable();

        this._browserSessionStatesById.set(id, new ReplaySubject(1));
        return this.browserSessionApi.getBrowserSessionStates(id).pipe(tap(ids => {
            this._browserSessionStatesById.get(id).next(ids);
        }), mergeMap(() => this._browserSessionStatesById.get(id).asObservable()));
    }

    mapSessionArtifactsToStates$(id: ExpeditionId): Observable<Map<BrowserSessionStateId, ArtifactId[]>> {
        return this.browserSessionApi.mapSessionArtifactsToStates(id).pipe(map(dto => {
            const result = new Map<BrowserSessionStateId, ArtifactId[]>();
            Object.entries(dto || {}).forEach(([key, value]) => {
                result.set(key as BrowserSessionStateId, value as ArtifactId[]);
            });
            return result;
        }));
    }

    makeStateFromArtifacts(stateArtifact: BrowserSessionStateArtifact, associatedArtifats: Artifact[]): State {
        let maybeScreenshot = associatedArtifats.find(artifact => artifact?.artifactType === 'Page.FullpageScreenshot' ||
            artifact?.artifactType === 'Page.Screenshot');

        let imageURL: ImageUrl | null = null;
        let thumbnailURL: ImageUrl | null = null;
        let screenshotId: ScreenshotId | null = null;
        if (maybeScreenshot?.data['screenshotId']) {
            //no api guard necessary as there is no http call, only crafting an url
            screenshotId = maybeScreenshot.data['screenshotId'];
            imageURL = this.imageService.getImageUrl(screenshotId, 'original');
            thumbnailURL = this.imageService.getImageUrl(screenshotId, 'medium');

        }
        return new State(stateArtifact.id, stateArtifact.browserSession, new Date(stateArtifact.creationTime), null, stateArtifact.name,
            associatedArtifats, imageURL || null, thumbnailURL, screenshotId, stateArtifact.provenance);
    }

    handleBrowserSessionStateEnd(artifactId: ArtifactId) {
        // refresh the entire state (and notify subscribers);
        this.artifactService.getArtifact$(artifactId).pipe(
            mergeMap(stateEndArtifact => this.getState$(stateEndArtifact.data['stateId'], true).pipe(take(1)))
        ).subscribe();
    }

    getState$(stateId: BrowserSessionStateId, queryFreshData: boolean = false): Observable<State> {
        if (queryFreshData || !isDefined(this._stateById.get(stateId))) {
            if (!this._stateById.has(stateId)) {
                this._stateById.set(stateId, new ReplaySubject<State>(1));
            }
            let stateSubject = this._stateById.get(stateId);

            return this.artifactService.getArtifact$(stateId).pipe(
                mergeMap(stateArtifact => {
                    let browserSessionStateArtifact = BrowserSessionStateArtifact.fromArtifact(stateArtifact);
                    return this.artifactService.getArtifactsInState$(browserSessionStateArtifact.id).pipe(
                        map(artifacts => [browserSessionStateArtifact, artifacts] as const)
                    );
                }),
                mergeMap(([stateArtifact, innerArtfacts]: readonly [BrowserSessionStateArtifact, Artifact[]]) => {
                    stateSubject.next(this.makeStateFromArtifacts(stateArtifact, innerArtfacts));
                    return stateSubject.asObservable();
                })
            );
        } else {
            return this._stateById.get(stateId).asObservable();
        }
    }

    /**
     * Get DOM Structure of a StateData artifact.
     */
    getStateDataDomStructure$(stateDataId: StateDataId): Observable<any> {
        return this.browserSessionApi.getStateDataDomStructure(stateDataId);
    }

    /**
     * Get meta information about a StateData artifact.
     */
    getStateDataInfo$(stateDataId: StateDataId): Observable<any> {
        return this.browserSessionApi.getStateDataInfo(stateDataId);
    }

    /**
     * Get node information a StateData DOM node.
     */
    getStateDataDomNode$(stateDataId: StateDataId, nodeId: string): Observable<any> {
        return this.browserSessionApi.getStateDataDomNode(stateDataId, nodeId);
    }


    //////////////
    /// Artifacts
    //////////////
    private createAndInitArtifactStore(browserSessionId: ExpeditionId, artifactId: ArtifactId, value?: Artifact) {
        let store = new ReplaySubject<Artifact>(1);
        if (!!value) {
            store.next(value);
        } else {
            this.artifactService.getArtifact$(artifactId).subscribe(artifact => store.next(artifact));
        }
        this._artifactById.set(artifactId, store);
    }

    private hasArtifactStore(id: ArtifactId): boolean {
        return this._artifactById.has(id);
    }

    private getArtifactStore(browserSessionId: ExpeditionId, artifactId: ArtifactId): Subject<Artifact> {
        if (!this._artifactById.has(artifactId)) {
            this.createAndInitArtifactStore(browserSessionId, artifactId);
        }
        return this._artifactById.get(artifactId);
    }

    private updateArtifactStore(browserSessionId: ExpeditionId, artifact: Artifact) {
        this.getArtifactStore(browserSessionId, artifact.id).next(artifact);
    }

    private createInteractiveDriverSpec(startUrl?: string): InteractiveDriverSpecification {
        const trimmedStartUrl = startUrl?.trim();
        return trimmedStartUrl ? {
            type: 'InteractiveDriverSpecification',
            startUrl: trimmedStartUrl
        } : {
            type: 'InteractiveDriverSpecification'
        };
    }

    private createInteractionPathDriverSpec(interactionPaths: InteractionPath[]): InteractionPathDriverSpecification {
        return {
            type: 'InteractionPathDriverSpecification',
            interactionPaths
        };
    }

    private createBrowserVehicleSpec(browser: Browser | Partial<Browser>, deviceId?: DeviceId, needCDP?: boolean): BrowserSpecification {
        const additionalRequirements = deviceId ? {
            type: 'property',
            [DevicePropertyNames.DeviceId]: deviceId
        } : undefined;

        return {
            browser,
            useProxy: false,
            type: 'BrowserSpecification',
            additionalDeviceRequirements: additionalRequirements,
            needCDP
        } as BrowserSpecification;
    }

    private createLiveBrowserExpeditionSpec(browser: Browser | Partial<Browser>, driverSpec: InteractiveDriverSpecification | InteractionPathDriverSpecification,
                                            requestedFacts?: FactRequest[], automationPolicy?: AutomationPolicyConfig,
                                            deviceId?: DeviceId, needCDP?: boolean): LiveExpeditionSpec {
        return {
            type: 'LiveExpeditionSpec',
            driverSpec,
            vehicleSpec: this.createBrowserVehicleSpec(browser, deviceId, needCDP),
            requestedFacts,
            automationPolicy,
            sticky: true
        };
    }

    private createHeadlessBrowserVehicleSpec(needCDP: boolean = true): BrowserSpecification {
        // TODO make browser configurable
        return {
            type: 'BrowserSpecification',
            browser: {browserType: 'CHROME'},
            headless: true,
            useProxy: false,
            needCDP
        } as BrowserSpecification;
    }

    private createHeadlessLiveExpeditionSpec(driverSpec: InteractiveDriverSpecification | InteractionPathDriverSpecification,
                                             requestedFacts?: FactRequest[], needCDP?: boolean): LiveExpeditionSpec {
        return {
            type: 'LiveExpeditionSpecification',
            driverSpec,
            vehicleSpec: this.createHeadlessBrowserVehicleSpec(needCDP),
            requestedFacts
        };
    }

    startBrowserExpeditionInProject$(projectId: ProjectId, browser: Partial<Browser>, startUrl?: string, requestedFacts?: FactRequest[],
                                     automationPolicy?: AutomationPolicyConfig): Observable<ExpeditionId> {
        const expeditionSpec = this.createLiveBrowserExpeditionSpec(
            browser,
            this.createInteractiveDriverSpec(startUrl),
            requestedFacts,
            automationPolicy
        );

        return this.browserSessionApi.startExpedition(projectId, expeditionSpec);
    }

    startBrowserExpeditionOnDevice$(projectId: ProjectId, deviceId: DeviceId, browser: Browser, startUrl?: string,
                                    requestedFacts?: FactRequest[], automationPolicy?: AutomationPolicyConfig,
                                    needCDP?: boolean): Observable<ExpeditionId> {
        const expeditionSpec = this.createLiveBrowserExpeditionSpec(
            browser,
            this.createInteractiveDriverSpec(startUrl),
            requestedFacts,
            automationPolicy,
            deviceId,
            needCDP
        );
        return this.browserSessionApi.startExpedition(projectId, expeditionSpec);
    }

    startHeadlessBrowserExpeditionInProject$(projectId: ProjectId, startUrl?: string, requestedFacts?: FactRequest[],
                                             needCDP?: boolean) {
        const effectiveStartUrl = startUrl?.trim() || DEFAULT_HEADLESS_START_URL;
        return this.browserSessionApi.startExpedition(projectId,
            this.createHeadlessLiveExpeditionSpec(this.createInteractiveDriverSpec(effectiveStartUrl), requestedFacts, needCDP));
    }

    startHeadlessBrowserExpeditionInProjectWithInteractionPaths$(projectId: ProjectId, interactionPaths: InteractionPath[],
                                                                 requestedFacts?: FactRequest[], needCDP?: boolean) {
        return this.browserSessionApi.startExpedition(projectId,
            this.createHeadlessLiveExpeditionSpec(this.createInteractionPathDriverSpec(interactionPaths), requestedFacts, needCDP));
    }

    startBrowserExpeditionInProjectWithInteractionPaths$(projectId: ProjectId, browser: Partial<Browser>,
                                                         interactionPaths: InteractionPath[], requestedFacts?: FactRequest[],
                                                         automationPolicy?: AutomationPolicyConfig): Observable<ExpeditionId> {
        const expeditionSpec = this.createLiveBrowserExpeditionSpec(
            browser,
            this.createInteractionPathDriverSpec(interactionPaths),
            requestedFacts,
            automationPolicy
        );
        return this.browserSessionApi.startExpedition(projectId, expeditionSpec);
    }

    startBrowserExpeditionOnDeviceWithInteractionPaths$(projectId: ProjectId, deviceId: DeviceId, browser: Browser,
                                                        interactionPaths: InteractionPath[], requestedFacts?: FactRequest[],
                                                        automationPolicy?: AutomationPolicyConfig,
                                                        needCDP?: boolean): Observable<ExpeditionId> {
        const expeditionSpec = this.createLiveBrowserExpeditionSpec(
            browser,
            this.createInteractionPathDriverSpec(interactionPaths),
            requestedFacts,
            automationPolicy,
            deviceId,
            needCDP
        );
        return this.browserSessionApi.startExpedition(projectId, expeditionSpec);
    }

    /**
     * Execute interaction path in expedition with given id.
     */
    executeInteractionPath$(expeditionId: ExpeditionId, interactionPath: any): Observable<void> {
        return this.browserSessionApi.interact(expeditionId, interactionPath);
    }


    /**
     * Stop expedition with givenn id.
     */
    stopExpedition$(expeditionId: ExpeditionId): Observable<void> {
        return this.browserSessionApi.stopExpedition(expeditionId);
    }

    getBrowserSessionArtifact$(browserSessionId: ExpeditionId, artifactId: ArtifactId): Observable<Artifact> {
        return this.getArtifactStore(browserSessionId, artifactId).asObservable();
    }

    getCDPClient$(expeditionId: ExpeditionId): Observable<CDPClient> {
        return CDPClient.create(expeditionId, this.authService);
    }


    /// State Generation
    getStateCollectorJob$(jobId: StateCollectorJobId): Observable<StateCollectorJob> {
        return withResultCache(() => this.browserSessionApi.getStateCollectorJob(jobId), this._stateCollectorJobById, jobId);
    }

    queryStateCollectorJobs$(projectId: ProjectId,
                             filters: {
                                 jobType?: string,
                                 since?: DateTime,
                                 until?: DateTime,
                                 status?: string
                             } = {},
                             limit: number | undefined = undefined): Observable<StateCollectorJob[]> {
        let queryMeta: QueryMetadata = {};
        queryMeta.filters = queryMeta.filters || {};
        if (filters.since) queryMeta.filters['startTime'] = filters.since;
        if (filters.until) queryMeta.filters['endTime'] = filters.until;
        if (filters.jobType) queryMeta.filters['jobType'] = filters.jobType;
        if (filters.status) queryMeta.filters['status'] = filters.status;
        if (limit) queryMeta.limit = limit;

        return this.browserSessionApi.queryStateCollectorJobs(projectId, queryMeta).fetch().pipe(
            map(result => result.result),
            // update caches while we have the data
            tap(jobs => {
                jobs.forEach(job => {
                    if (isDefined(this._stateCollectorJobById[job.id])) {
                        this._stateCollectorJobById[job.id].next(job);
                    } else {
                        this._stateCollectorJobById[job.id] = new ReplaySubject<StateCollectorJob>(1);
                        this._stateCollectorJobById[job.id].next(job);
                    }
                });
            })
        );
    }

    getStateCollection$(id: StateCollectionId): Observable<StateCollection> {
        return withResultCache(() => this.browserSessionApi.getStateCollection(id), this._stateCollectionById, id);
    }

    getStateCollections$(projectId: ProjectId): Observable<StateCollection[]> {
        return this.browserSessionApi.getStateCollections(projectId);
    }

    getStateCollectorSpec$(id: StateCollectorSpecId): Observable<StateCollectorSpec> {
        return this.browserSessionApi.getStateCollectorSpec(id);
    }

    startStateCollectorJob$(projectId: ProjectId, collectorSpec: NewStateCollectorJobSpec): Observable<StateCollectorJobId> {
        return this.browserSessionApi.startStateCollectorJob(projectId, collectorSpec);
    }

}
